// StakingRoom.js
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import TokenLogo from './TokenLogo';
import { motion } from 'framer-motion';
import styles from './StakingRoom.styles';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const API_STAKING_URL = 'https://www.sharedvision-partners.online/v1/API/staking.php';
//const API_GET_USER_URL = 'https://www.sharedvision-partners.online/v1/API/get_user.php'; not needed again
const SVP_CONVERSION_RATE = 100000; // 1 USD = 100,000 SVP tokens

function StakingRoom({ userDetails }) {
  const currentUser = userDetails?.username;

  // States
  const [basicUserData, setBasicUserData] = useState(null);
  const [stakingData, setStakingData] = useState(null);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [convertAmount, setConvertAmount] = useState('');
  const [buyAmount, setBuyAmount] = useState('');
  const [sellAmount, setSellAmount] = useState('');
  const [stakeAmount, setStakeAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [chartData, setChartData] = useState([]);
  const [modalMessage, setModalMessage] = useState(null);
  const [modalMessage2, setModalMessage2] = useState(null);
   const [leaderboardOpen, setLeaderboardOpen] = useState(false);
   const [initialDataFetched, setInitialDataFetched] = useState(false); // ✅ NEW STATE


  const avatarUrl = (username) =>
    `https://api.dicebear.com/7.x/thumbs/svg?size=64&seed=${username}`;

  // Combined user data for display
  const combinedUserData = stakingData && basicUserData
    ? {
        username: basicUserData.username,
        email: basicUserData.email,
        balance: Number(basicUserData.balance),
        fundBalance: Number(stakingData.fundBalance),
        staked: Number(stakingData.staked),
        earned: Number(stakingData.earned),
        tokenWallet: Number(stakingData.tokenWallet),
        status: stakingData.status? stakingData.status : 'Hold',
        totalEarned: Number(stakingData.totalEarned) || 0,
        stakingStartDate: stakingData.stakingStartDate ? new Date(stakingData.stakingStartDate) : null,
        stakeStartTime: stakingData.stakeStartTime ? new Date(stakingData.stakeStartTime) : null,
        stakingEndDate: stakingData.stakingEndDate ? new Date(stakingData.stakingEndDate) : null,
        stakingEndTime: stakingData.stakeEndTime ? new Date(stakingData.stakeEndTime) : null,
      
      }
    : null;

  // Achievements badges
  const getAchievements = ({ balance, staked, earned }) => {
    const badges = [];
    if (balance >= 1000) badges.push('💰 Rich Wallet');
    if (staked >= 100) badges.push('📥 Staking Champ');
    if (earned >= 200) badges.push('📈 Yield Farmer');
    return badges.join(', ') || 'None';
  };

  const showModalMessage2 = (message) => {
    setModalMessage2(message);
    setTimeout(() => setModalMessage2(null), 5000);
  };

  const isStakingActive = () => {
    if (!stakingData?.stakingEndDate) return false;
    const now = new Date();
    return now < new Date(stakingData.stakingEndDate);
  };
  

  // Fetch staking data for the current user
  const fetchStakingData = useCallback(async (username) => {
    try {
      const res = await axios.post(API_STAKING_URL, { username, action: 'getFundData' });
      if (res.data && res.data.username) {
        return {
          fundBalance: Number(res.data.fundBalance) || 0,
          staked: Number(res.data.staked) || 0,
          earned: Number(res.data.earned) || 0,
          tokenWallet: Number(res.data.tokenWallet) || 0,
          status: res.data.status || 'Hold',
            totalEarned: Number(res.data.totalEarned) || 0,
            stakingStartDate: res.data.stakingStartDate ? new Date(res.data.stakingStartDate) : null,
            stakingEndDate: res.data.stakingEndDate ? new Date(res.data.stakingEndDate) : null,
            stakeEndTime: res.data.stakeEndTime ? new Date(res.data.stakeEndTime) : null,
          stakeStartTime: res.data.stakeStartTime || null,
          username: res.data.username,
        };
      }
      return null;
    } catch (err) {
      console.error('Error fetching staking data:', err);
      return null;
    }
  }, []);

  // Fetch leaderboard users
  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await axios.post(API_STAKING_URL, { action: 'getLeaderboard' });
      if (res.data && Array.isArray(res.data)) {
        const normalizedUsers = res.data.map((user) => ({
          username: user.username,
          email: user.email || '',
          balance: Number(user.balance) || 0,
          fundBalance: Number(user.fundBalance) || 0,
          staked: Number(user.staked) || 0,
          earned: Number(user.earned) || 0,
          tokenWallet: Number(user.tokenWallet) || 0,
          stakeStartTime: user.stakeStartTime ? new Date(user.stakeStartTime) : null,
        }));

        const usersMap = {};
        normalizedUsers.forEach((u) => {
          usersMap[u.username] = u;
        });
        setUsers(usersMap);
      }
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  }, []);

  // Consolidated backend update function for both user data and staking data
  const updateUserBackend = async (newBasicUserData, newStakingData) => {
    try {
        const response = await fetch(API_STAKING_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'updateBalanceAndFundBalance',
          username: newBasicUserData.username,
          newBalance: newBasicUserData.balance,
          newFundBalance: newStakingData.fundBalance,
        }),
      });

      const result = await response.json();
      if (result.success) {
        console.log(result.success);
      } else {
        console.error(result.error);
      }
    } catch (err) {
      console.error('Failed to update user backend:', err);
    }

  };

  // Consolidated backend update function for staking data
  const updateStakingBackend = async ({ username, newFundBalance, newTokenWallet }) => {
    try {
      const response = await fetch(API_STAKING_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'updateStakingData',
          username,
          newFundBalance,
          newTokenWallet,
        }),
      });
  
      const result = await response.json();
      if (result.success) {
        console.log(result.success);
      } else {
        console.error(result.error);
      }
    } catch (err) {
      console.error('Failed to update staking backend:', err);
    }
  };
  

  // Initialization
  useEffect(() => {
    if (!currentUser) return;

     const timeoutId = setTimeout(() => {
      const init = async () => {
        setLoading(true);

      setBasicUserData({
        username: userDetails.username,
        email: userDetails.email || '',
        balance: Number(userDetails.balance) || 0,
      });

      const stakingInfo = await fetchStakingData(currentUser);
      if (stakingInfo) {
        setStakingData(stakingInfo);
      } else {
        const initialStakingData = {
          username: currentUser,
          fundBalance: 0,
          staked: 0,
          earned: 0,
          tokenWallet: 0,
          stakeStartTime: null,
        };
        await updateStakingBackend(initialStakingData);
        setStakingData(initialStakingData);
      }

      await fetchLeaderboard();
    setInitialDataFetched(true); // ✅ Mark initial load done
        setLoading(false);
      };

      init();
    }, 1500); // 1.5 seconds delay

     return () => clearTimeout(timeoutId);
  }, [currentUser, userDetails, fetchStakingData, fetchLeaderboard]);

  // ✅ Delay 10s Polling Until After Initial Fetch
  useEffect(() => {
    if (!initialDataFetched || !basicUserData) return;
 const interval = setInterval(async () => {
      try {
        const stakingInfo = await fetchStakingData(currentUser);
        if (stakingInfo) {
          setStakingData(stakingInfo);
          setChartData((prevData) => [
            ...prevData.slice(-9),
            {
            timestamp: new Date().toLocaleTimeString(),
              value: stakingInfo.staked + stakingInfo.earned,
            },
          ]);
          setUsers((prevUsers) => ({
            ...prevUsers,
            [currentUser]: {
              ...prevUsers[currentUser],
              ...stakingInfo,
              balance: basicUserData.balance,
              email: basicUserData.email,
            },
          }));
        }
        await fetchLeaderboard();
      } catch (err) {
        console.error('Error updating staking data:', err);
      }
    }, 10000); // every 10 seconds

      return () => clearInterval(interval);
  }, [initialDataFetched, currentUser, basicUserData, fetchStakingData, fetchLeaderboard]);

    // Chart data
  const lineChartData = {
    labels: chartData.map((d) => d.timestamp),
    datasets: [
      {
        label: 'Staked + Earned',
          data: chartData.map((d) => d.value),
        borderColor: 'rgba(75, 192, 192, 0.9)',
        backgroundColor: 'rgba(75, 192, 192, 0.3)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 6,
      },     ],
  };
    
   useEffect(() => {
  if (!currentUser) return;

  const timeoutId = setTimeout(() => {
    const init = async () => {
      // your init logic here
    };
    init();
  }, 1500);

  return () => clearTimeout(timeoutId);
}, [currentUser, userDetails, fetchStakingData, fetchLeaderboard]);


  // Refresh staking data every 10 seconds
  useEffect(() => {
    if (!basicUserData) return;

    const interval = setInterval(async () => {
      try {
        const stakingInfo = await fetchStakingData(currentUser);
        if (stakingInfo) {
          setStakingData(stakingInfo);
          setChartData((prevData) => [
            ...prevData.slice(-9),
            {
              timestamp: new Date().toLocaleTimeString(),
              value: stakingInfo.staked + stakingInfo.earned,
            },
          ]);

          setUsers((prevUsers) => ({
            ...prevUsers,
            [currentUser]: {
              ...prevUsers[currentUser],
              ...stakingInfo,
              balance: basicUserData.balance,
              email: basicUserData.email,
            },
          }));
        }
        await fetchLeaderboard();
      } catch (err) {
        console.error('Error updating staking data:', err);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [currentUser, basicUserData, fetchStakingData, fetchLeaderboard]);

  // Convert balance <> fundBalance
  const convertBalance = async (toFundBalance) => {
    const amount = parseFloat(convertAmount);
    if (isNaN(amount) || amount <= 0) {
      setModalMessage('Invalid amount to convert.');
      return;
    }
    if (!basicUserData || !stakingData) return;

    if (toFundBalance) {
      if (amount > basicUserData.balance) {
        setModalMessage('Insufficient balance.');
        return;
      }
      const newBasicUserData = { ...basicUserData, balance: basicUserData.balance - amount };
      const newStakingData = { ...stakingData, fundBalance: stakingData.fundBalance + amount };

      setBasicUserData(newBasicUserData);
      setStakingData(newStakingData);
      setUsers((prev) => ({
        ...prev,
        [newBasicUserData.username]: {
          ...prev[newBasicUserData.username],
          balance: newBasicUserData.balance,
          fundBalance: newStakingData.fundBalance,
        },
      }));
      setConvertAmount('');

      await updateUserBackend(newBasicUserData, newStakingData); // Updated backend call
    } else {
      if (amount > stakingData.fundBalance) {
        setModalMessage('Insufficient fund balance.');
        return;
      }
      const newBasicUserData = { ...basicUserData, balance: basicUserData.balance + amount };
      const newStakingData = { ...stakingData, fundBalance: stakingData.fundBalance - amount };

      setBasicUserData(newBasicUserData);
      setStakingData(newStakingData);
      setUsers((prev) => ({
        ...prev,
        [newBasicUserData.username]: {
          ...prev[newBasicUserData.username],
          balance: newBasicUserData.balance,
          fundBalance: newStakingData.fundBalance,
        },
      }));
      setConvertAmount('');

      await updateUserBackend(newBasicUserData, newStakingData); // Updated backend call
    }
  };
  

  
  // Stake tokens
  const stakeTokens = async () => {
    const amount = parseFloat(stakeAmount);
    const now = new Date();

    if (isNaN(amount) || amount <= 0) {
      setModalMessage('Invalid staking amount.');
      return;
    }
    if (!stakingData) return;
  
    if (!stakingData || amount > stakingData.tokenWallet) {
      setModalMessage('Insufficient SVP tokens in your wallet.');
      return;
    }

    // Check staking is allowed (not active)
  if (stakingData.stakingEndDate && new Date(stakingData.stakingEndDate) > now) {
    setModalMessage('You already have an active stake. Wait until it ends.');
    return;
  }

  const newStaked = stakingData.staked + amount;
  const newTokenWallet = stakingData.tokenWallet - amount;
  const startDate = now.toISOString();
  const endDate = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000).toISOString(); // 60 days
  const newStakingData = {
    ...stakingData,
    staked: newStaked,
    tokenWallet: newTokenWallet,
    stakingStartDate: startDate,
    stakingEndDate: endDate,
    earned: 0,
    totalEarned: 0,
  };

  setStakingData(newStakingData);
  setUsers((prev) => ({
    ...prev,
    [newStakingData.username]: {
      ...prev[newStakingData.username],
      staked: newStaked,
      tokenWallet: newTokenWallet,
      stakingStartDate: startDate,
      stakingEndDate: endDate,
      earned: 0,
      totalEarned: 0,
    },
  }));

  setStakeAmount('');

  await updateStakingBackend(newStakingData);
  
    try {
      const res = await axios.post(API_STAKING_URL, {
        action: 'stakeTokens',
        username: currentUser,
        amount,
      });
  
      if (res.data.success) {
        showModalMessage2('✅ Tokens successfully staked!');
  
        // Refresh user staking data
        const updatedData = await fetchStakingData(currentUser);
        setStakingData(updatedData);
  
        // Optionally refresh leaderboard
        await fetchLeaderboard();
      } else {
        setModalMessage(res.data.error || 'An error occurred.');
      }
    } catch (err) {
      console.error(err);
      setModalMessage('Staking request failed.');
    }
  
    setStakeAmount('');
  };
  
 
  

  // Withdraw earned rewards
  const withdrawEarned = async () => {
    if (!stakingData || stakingData.earned <= 0) {
      setModalMessage('No earnings to withdraw.');
      return;
    }

    const newBalance = basicUserData.balance + stakingData.earned;
    const newStakingData = { ...stakingData, earned: 0 };

    setBasicUserData({ ...basicUserData, balance: newBalance });
    setStakingData(newStakingData);

    setUsers((prev) => ({
      ...prev,
      [basicUserData.username]: {
        ...prev[basicUserData.username],
        balance: newBalance,
        earned: 0,
      },
    }));

    await updateUserBackend({ ...basicUserData, balance: newBalance });
    await updateStakingBackend(newStakingData);
  };

  // Transfer fundBalance to another user
  const transferFunds = async () => {
    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      setModalMessage('Invalid transfer amount.');
      return;
    }
    if (!stakingData) return;
    if (amount > stakingData.fundBalance) {
      setModalMessage('Insufficient fund balance to transfer.');
      return;
    }
    if (recipient === currentUser) {
      setModalMessage('Cannot transfer funds to yourself.');
      return;
    }
    if (!users[recipient]) {
      setModalMessage('Recipient user does not exist.');
      return;
    }

    const recipientData = users[recipient];

    // Update sender
    const newSenderFundBalance = stakingData.fundBalance - amount;
    const newSenderData = { ...stakingData, fundBalance: newSenderFundBalance };

    // Update recipient
    const newRecipientFundBalance = recipientData.fundBalance + amount;
    const newRecipientUserData = { ...recipientData, fundBalance: newRecipientFundBalance };

    setStakingData(newSenderData);
    setUsers((prev) => ({
      ...prev,
      [recipient]: newRecipientUserData,
      [currentUser]: newSenderData,
    }));

    setTransferAmount('');
    setRecipient('');

    await updateStakingBackend(newSenderData);
    await updateStakingBackend(newRecipientUserData);
  };

  // Buy SVP tokens
  const buyTokens = async () => {
    const amountInUSD = parseFloat(buyAmount);
    if (isNaN(amountInUSD) || amountInUSD <= 0) {
      showModalMessage2('Invalid amount to buy tokens.');
      return;
    }
  
    if (amountInUSD < 1) {
        showModalMessage2('You must buy at least $1 worth of SVP tokens.');
      return;
    }
  
    if (amountInUSD > stakingData.fundBalance) {
        showModalMessage2('Insufficient fund balance.');
      return;
    }
  
    const svpAmount = amountInUSD * SVP_CONVERSION_RATE;
    const newFundBalance = stakingData.fundBalance - amountInUSD;
    const newTokenWallet = stakingData.tokenWallet + svpAmount;
  
    const newStakingData = {
      ...stakingData,
      fundBalance: newFundBalance,
      tokenWallet: newTokenWallet,
    };
  
    setStakingData(newStakingData);
    setBuyAmount('');
  
    await updateStakingBackend({
      username: currentUser,
      newFundBalance,
      newTokenWallet,
    });
  
    await logActivity('Bought SVP tokens');

    showModalMessage2(`✅ Successfully purchased ${svpAmount.toLocaleString()} SVP tokens!` +
      `\nfor: $${amountInUSD.toFixed(2)} USD.`);
  };
  

  // Sell SVP tokens
  const sellTokens = async () => {
    const amountInTokens = parseFloat(sellAmount);
    if (isNaN(amountInTokens) || amountInTokens <= 0) {
        showModalMessage2('Invalid amount to sell tokens.');
      return;
    }
  
    if (amountInTokens > stakingData.tokenWallet) {
        showModalMessage2('Insufficient SVP tokens.');
      return;
    }
  
    const amountInUSD = amountInTokens / SVP_CONVERSION_RATE;
    const newFundBalance = stakingData.fundBalance + amountInUSD;
    const newTokenWallet = stakingData.tokenWallet - amountInTokens;
  
    const newStakingData = {
      ...stakingData,
      fundBalance: newFundBalance,
      tokenWallet: newTokenWallet,
    };
  
    setStakingData(newStakingData);
    setSellAmount('');
  
    await updateStakingBackend({
      username: currentUser,
      newFundBalance,
      newTokenWallet,
    });
  
    await logActivity('Sold SVP tokens');
    showModalMessage2(`✅ Successfully sold ${amountInTokens.toLocaleString()} SVP tokens for $${amountInUSD.toFixed(2)}!`);

  };
  

  // Log activity
  const logActivity = async (activity) => {
    try {
      await axios.post(API_STAKING_URL, {
        username: currentUser,
        action: 'logActivity',
        activity,
      });
    } catch (err) {
      console.error('Error logging activity:', err);
    }
  }; 

  if (loading || !combinedUserData) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', fontSize: 20, color: '#666',
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={styles.container}>
      
         <header style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <TokenLogo style={{ width: 80, height: 80 }} />
  
            <motion.div style={styles.welcome}>
        Staking Room - {combinedUserData.username}
        </motion.div>
      </header>

    {/* User Info Card */}
<section style={styles.summarySection}>
  {/* Avatar and Info Container */}
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 20,
  }}>
    <img
      className="staking-avatar"
      src={avatarUrl(combinedUserData.username)}
      alt="User Avatar"
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '3px solid #2980b9',
        boxShadow: '0 4px 12px rgba(41, 128, 185, 0.4)',
        objectFit: 'cover',
        flexShrink: 0,
      }}
    />

  {/* User Info */}
    <div style={{
       flex: '1 1 0',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      width: '100%',
      
    }}>
      {/* Each Row as Block for Better Stacking on Mobile */}
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
        <p style={{ flex: 1, margin: 0 }}><strong>Status:</strong> <span style={{ color: '#34495e' }}>{combinedUserData.status || 'In-Active'}</span></p>
        <p style={{ flex: 1, margin: 0 }}><strong>Achievements:</strong> <span style={{ color: '#f39c12' }}>{getAchievements(combinedUserData)}</span></p>
      </div>

      {/* Row 2 */}
     <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
        <p style={{ flex: 1, margin: 0 }}><strong>Balance:</strong> <span style={{ color: '#27ae60' }}>{combinedUserData.balance.toFixed(2) } </span></p>
        <p style={{ flex: 1, margin: 0 }}><strong>Earn APX:</strong> <span style={{ color: '#34495e' }}>{combinedUserData.totalEarned || '0'}</span></p>
      </div>

      {/* Row 3 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
        <p style={{ flex: 1, margin: 0 }}><strong>Fund Bal:</strong> <span style={{ color: '#2980b9' }}>${combinedUserData.fundBalance.toFixed(2) || '0.00'}</span></p>
        <p style={{ flex: 1, margin: 0 }}><strong>Tokens:</strong> <span style={{ color: '#267e40' }}>{combinedUserData?.tokenWallet.toFixed(0) || '0'}</span></p>
      </div>

      {/* Row 4 */}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
        <p style={{ flex: 1, margin: 0 }}><strong>Staked:</strong> <span style={{ color: '#e67e22' }}>{combinedUserData.staked.toFixed(2)}</span></p>
        <p style={{ flex: 1, margin: 0 }}><strong>Earned:</strong> <span style={{ color: '#9b59b6' }}>{combinedUserData.earned.toFixed(2)}</span></p>
      </div>
      
      {/* Row 5 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <p style={{ margin: 0 }}><strong>Stake day:</strong> <span style={{ color: '#7f8c8d' }}>{combinedUserData.stakingStartDate ? new Date(combinedUserData.stakingStartDate).toLocaleString() : 'Not yet staking'}</span></p>
      </div>

           {/* Active Stake Note */}
     {isStakingActive() && stakingData.stakingEndDate && (
        <p style={{ marginTop: 4, color: '#34495e', textAlign: 'center' }}>
          ⏳ You have an active stake until <strong>{new Date(stakingData.stakingEndDate).toLocaleString()}</strong>
        </p>
      )}
    </div>
  </div>
</section>


      {/* Conversion Controls */}
      <section style={styles.controlsSection}>
        <div style={styles.controlCard}>
          <h3 style={styles.controlTitle}>Convert Balance ↔ Fund Balance</h3>
          <input
            type="number"
            placeholder="Amount"
            value={convertAmount}
            onChange={(e) => setConvertAmount(e.target.value)}
            style={styles.input}
          />
          <div style={styles.buttonGroup}>
            <button
              onClick={() => convertBalance(true)}
              style={{ ...styles.button, ...styles.blueButton }}
              title="Convert from Balance to Fund Balance"
            >
              To Fund Balance
            </button>
            <button
              onClick={() => convertBalance(false)}
              style={{ ...styles.button, ...styles.greenButton }}
              title="Convert from Fund Balance to Balance"
            >
              To Balance
            </button>
          </div>
        </div>


        {/* Buy SVP Tokens */}
        <div style={styles.controlCard}>
            <h3 style={styles.controlTitle}>Buy SVP Tokens</h3>
            <input
              type="number"
                placeholder="Amount in USD"
                value={buyAmount}
                onChange={(e) => setBuyAmount(e.target.value)}
                style={styles.input}
            />
              {/* Show conversion result */}
  {buyAmount && (
    <div style={{ marginTop: 5, fontSize: 14 }}>
     <strong>{(parseFloat(buyAmount) * SVP_CONVERSION_RATE).toLocaleString()} SVP</strong>
    </div>
  )}
            <button
                onClick={buyTokens}
                style={{ ...styles.button, ...styles.blueButton }}
                title="Buy SVP tokens with USD balance" 
            >   
                Buy SVP
            </button>
        </div>
        {/* Sell SVP Tokens */}
        <div style={styles.controlCard}>
            <h3 style={styles.controlTitle}>Sell SVP Tokens</h3>
            <input
                type="number"
                placeholder="Amount in SVP Tokens"  
                value={sellAmount}
                onChange={(e) => setSellAmount(e.target.value)}
                style={styles.input}
            />
            {/* Show conversion result */}
            {sellAmount && (
                <div style={{ marginTop: 5, fontSize: 14 }}>
                    <strong>${(parseFloat(sellAmount) / SVP_CONVERSION_RATE).toFixed(2)} USD</strong>
                </div>
            )}
            <button
                onClick={sellTokens}
                style={{ ...styles.button, ...styles.redButton }}   
                title="Sell SVP tokens for USD balance"
            >
                Sell SVP
            </button>
        </div>
        

        {/* Stake Tokens */}
        <div style={styles.controlCard}>
          <h3 style={styles.controlTitle}>Stake Tokens</h3>
          <input
            type="number"
            placeholder="Stake Amount"
            value={stakeAmount}
            onChange={(e) => setStakeAmount(e.target.value)}
            style={styles.input}
          />
           
        {/* Conversion Display */}
  {stakeAmount && !isNaN(parseFloat(stakeAmount)) && (
    <div style={{ marginBottom: 15, fontSize: 14, color: '#555' }}>
      ≈ <strong>${(parseFloat(stakeAmount) / SVP_CONVERSION_RATE).toFixed(2)} USD</strong>
    </div>
  )}
          <button
            onClick={stakeTokens}
            style={{ ...styles.button, ...styles.orangeButton }}
            title="Stake fund balance tokens"
          >
            Stake
          </button>
         
       </div>

        {/* Withdraw Earnings */}
        <div style={{ ...styles.controlCard, justifyContent: 'center', alignItems: 'center' }}>
          <h3 style={styles.controlTitle}>Withdraw Earnings</h3>
          <button
            onClick={withdrawEarned}
            style={{ ...styles.button, ...styles.purpleButton }}
            title="Withdraw earned tokens to balance"
          >
            Withdraw Earned
          </button>
        </div>
      </section>

      {/* Transfer Funds */}
      <section style={styles.transferSection}>
        <h3 style={styles.sectionTitle}>Transfer Fund Balance to User</h3>
        <input
          type="text"
          placeholder="Recipient username"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          style={styles.input}
        />
        <input
          type="number"
          placeholder="Amount"
          value={transferAmount}
          onChange={(e) => setTransferAmount(e.target.value)}
          style={styles.input}
        />
        <button
          onClick={transferFunds}
          style={{ ...styles.button, ...styles.redButton }}
          title="Transfer fund balance tokens"
        >
          Transfer
        </button>
      </section>

      {/* Staked + Earned Chart */}
      <section style={styles.chartSection}>
        <h3 style={styles.sectionTitle}>Staked + Earned Over Time</h3>
        <Line data={lineChartData} />
      </section>

      {/* Leaderboard */}
      <section style={styles.leaderboardSection}>
        <button
          onClick={() => setLeaderboardOpen((open) => !open)}
          style={{ ...styles.button, ...styles.leaderboardToggleButton }}
        >
          {leaderboardOpen ? 'Hide' : 'Show'} Leaderboard
        </button>

        {leaderboardOpen && (
          <div style={styles.leaderboardContainer} role="list">
            {Object.values(users)
              .sort((a, b) => b.staked - a.staked)
              .map((user, idx) => (
                <div key={user.username} style={styles.leaderboardItem} role="listitem">
                  <img
                    src={avatarUrl(user.username)}
                    alt={`${user.username} avatar`}
                    style={styles.avatar}
                  />
                  <div style={styles.leaderboardText}>
                    <strong>{user.username}</strong> — Balance: {user.balance.toFixed(2)} | Fund: {user.fundBalance.toFixed(2)} | Staked: {user.staked.toFixed(2)} | Earned: {user.earned.toFixed(2)}
                  </div>
                  <div style={styles.rank}>#{idx + 1}</div>
                </div>
              ))}
          </div>
        )}
      </section>

      {/* Modal message */}
      {modalMessage && (
        <div
          onClick={() => setModalMessage(null)}
          style={styles.modalMessage}
          role="alert"
          aria-live="assertive"
        >
          {modalMessage} (Click to dismiss)
        </div>
      )}

{modalMessage2 && (
  <div style={{
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    position: 'fixed',
    top: '30%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1000,
    color: '#333',
    minWidth: '300px',
    textAlign: 'center',
  }}>
    <p>{modalMessage2}</p>
    <button
      style={{
        marginTop: '10px',
        padding: '6px 16px',
        background: '#007BFF',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
      onClick={() => setModalMessage2(null)}
    >
      Close
    </button>
  </div>
)}


      
    </div>
  );
}

// Styles below the component for clarity and neatness

export default StakingRoom;